import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import store from "@/store";
import Home from "@/views/Home.vue";
import Login from "@/views/Login.vue";
import MyUser from "@/views/MyUser.vue";
import { eventBus } from "@/eventBus";
import isExtranetHostname from "@/helpers/isExtranet";
import { useSplashScreen } from "@/composables/useSplashScreen";
import { setSwipeBackEnabled } from "@/services/swipeBack";
import { isSessionVerified, markSessionVerified, expireSession, resolveLoginPath } from "@/services/sessionManager";

const { hideSplash, isMobile } = useSplashScreen();
let isAppInitialized = false;
let splashPendingHide = false;
async function setSwipeBackEnabledSafe(enabled, timeoutMs = 300) {
  try {
    await Promise.race([
      setSwipeBackEnabled(enabled),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeoutMs)),
    ]);
  } catch (e) {
    console.warn('[SwipeBack] gagal/timeout, lanjut navigasi:', e);
  }
}


let triggeredComponent = null;
eventBus.on("navigation-triggered", (component) => {
  triggeredComponent = component;
});

/**
 * Utility to validate multiple route guards.
 * 
 * @param {Function[]} guards - Array of guard functions to execute.
 * @param {string} [operator='or'] - Logic operator ('or' | 'and') to determine validation success.
 * @returns {Function} A navigation guard function that returns true or a redirect object.
 */
const guardCheck = (guards, operator = "or") => {
  return function () {
    const validate = guards.map((guard_func) => {
      return guard_func();
    });

    if (operator == "or") {
      return validate.includes(true) ? true : { name: "Home" };
    }

    if (operator == "and") {
      return validate.includes(false) ? { name: "Home" } : true;
    }
  };
};

/**
 * Navigation guard to check user permission for a specific module.
 * 
 * @param {string} moduleName - The name of the module to verify access for.
 * @returns {Function} An asynchronous navigation guard function.
 */
const checkModulePermission = (moduleName) => {
  return async (to, from, next) => {
    const userLevel = store.state.Auth.currUser?.level || localStorage.getItem("level");

    if (userLevel === "root") {
      return next();
    }

    let userModules = store.state.Auth.userInfo?.modules;

    // On hard refresh, Vuex state is empty even if token exists in localStorage.
    // Re-fetch auth data if modules are not yet loaded (not array) or empty.
    if (!Array.isArray(userModules) || userModules.length === 0) {
      try {
        await store.dispatch("Auth/auth_check");
        userModules = store.state.Auth.userInfo?.modules;

        if (!Array.isArray(userModules)) {
          console.warn(`[Router] No modules array found after auth_check for route: ${to.path}`, {
            userLevel: userLevel,
            hasUserInfo: !!store.state.Auth.userInfo,
            modulesValue: store.state.Auth.userInfo?.modules,
          });
          return next({ name: "PermissionDenied" });
        }
      } catch (err) {
        // 401 ditangani interceptor. Offline: server tetap yang menegakkan izin sebenarnya.
        if (!err?.response) return next();
        return next({ name: "PermissionDenied" });
      }
    }

    if (userModules.length === 0) {
      console.warn(`[Router] User has empty modules array for route: ${to.path}`);
      return next({ name: "PermissionDenied" });
    }

    const module = userModules.find((mod) => mod.module_name === moduleName);

    if (!module || (module.read !== 1 && module.write !== 1)) {
      console.warn(`[Router] Permission denied for module "${moduleName}" on route: ${to.path}`, {
        moduleExists: !!module,
        module: module ? { read: module.read, write: module.write } : "not found",
        availableModules: userModules.map((m) => m.module_name),
      });
      return next({ name: "PermissionDenied" });
    }

    return next();
  };
};

/**
 * Combines module permission check with additional route guards.
 * 
 * @param {string} moduleName - The module name to check permission for.
 * @param {Function[]} [extraGuards=[]] - Additional guard functions to run if permission check passes.
 * @returns {Function} An asynchronous navigation guard function.
 */
const combineGuards = (moduleName, extraGuards = []) => {
  return async (to, from, next) => {
    const permissionGuard = checkModulePermission(moduleName);
    await permissionGuard(to, from, (permResult) => {
      if (permResult !== undefined && permResult !== true) {
        return next(permResult);
      }

      if (extraGuards.length > 0) {
        const extraGuard = guardCheck(extraGuards);
        const extraResult = extraGuard();

        if (extraResult !== true) {
          return next(extraResult);
        }
      }

      return next();
    });
  };
};

/**
 * Guard to check if point system is active (not NONACTIVE).
 * Blocks access to all point-related features if NONACTIVE.
 * 
 * @returns {Function} An async navigation guard function.
 */
const requirePointSystemActive = () => {
  return async (_to, _from, next) => {
    const businessProfile = store.state.Auth.selectedBusinessProfile;
    if (businessProfile?.corporate_id) {
      try {
        await store.dispatch('FeatureFlag/fetchFeatureFlag', businessProfile.corporate_id);
      } catch (err) {
        return next();
      }
    }

    const flag = store.state.FeatureFlag?.pointControl?.flag || 'DECENTRALIZE';

    if (flag === 'NONACTIVE') {
      return next();
    }

    return next();
  };
};

/**
 * Guard to check if Point Plan should be accessible.
 * - CENTRALIZE: Only corporate can access
 * - DECENTRALIZE: Only merchant can access
 * - NONACTIVE: Allow navigation so FeatureGate displays upsell screen
 * 
 * @returns {Function} An async navigation guard function.
 */
const checkPointPlanAccess = () => {
  return async (_to, _from, next) => {
    const businessProfile = store.state.Auth.selectedBusinessProfile;
    if (businessProfile?.corporate_id) {
      try {
        await store.dispatch('FeatureFlag/fetchFeatureFlag', businessProfile.corporate_id);
      } catch (err) {
        if (alreadyInMerchant()) {
          return next();
        }
        return next({ name: 'PermissionDenied' });
      }
    }

    const flag = store.state.FeatureFlag?.pointControl?.flag || 'DECENTRALIZE';

    // NONACTIVE: Allow navigation for FeatureGate upsell
    if (flag === 'NONACTIVE') {
      return next();
    }

    // CENTRALIZE: Only corporate can access
    if (flag === 'CENTRALIZE') {
      if (alreadyInCorporate()) {
        return next();
      }
      return next({ name: 'PermissionDenied' });
    }

    // DECENTRALIZE: Only merchant can access
    if (flag === 'DECENTRALIZE') {
      if (alreadyInMerchant()) {
        return next();
      }
      return next({ name: 'PermissionDenied' });
    }

    return next();
  };
};

/**
 * Guard to check if a tenant has been selected in Extranet context.
 * Used for tenant-admin routes (Branch, Merchant, Connections management).
 * 
 * @returns {Function} An async navigation guard function.
 */
const requireTenantSelected = () => {
  return async (_to, _from, next) => {
    const hasTenant = store.getters['extranet/hasTenantSelected'];

    if (!hasTenant) {
      return next({ name: 'ExtranetTenantAdmin' });
    }

    return next();
  };
};

const routes = [
  {
    path: "/dashboard",
    name: "Home",
    component: Home,
    meta: {
      requiresAuth: true,
    },
    beforeEnter: requireBackoffice
  },
  {
    path: "/my-user",
    name: "MyUser",
    component: MyUser,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/membertier/threshold",
    name: "ThresholdSettings",
    meta: { requiresAuth: true },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/ThresholdSettings.vue"),
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
  },
  {
    path: "/backoffice/login",
    name: "BackofficeLogin",
    component: () => import("../views/Auth/BackofficeLogin.vue"),
  },
  {
    path: "/member/login",
    name: "MemberLogin",
    component: () => import("../views/Auth/MemberLogin.vue"),
  },
  //member public policies
  {
    path: "/public/policies",
    name: "PublicPolicies",
    component: () => import("../views/PublicPolicies.vue"),
  },
  {
    path: "/privacy-policy",
    redirect: "/public/policies?tab=privacy",
  },
  {
    path: "/terms-conditions",
    redirect: "/public/policies?tab=terms",
  },
  {
    path: "/",
    redirect: () => {
      const isNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform();
      return isNative ? "/member/login" : "/login";
    },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPassword.vue"),
  },
  {
    path: "/reset-password/:token",
    name: "ResetPassword",
    component: () => import("../views/ResetPassword.vue"),
  },
  {
    path: "/:merchant_id/register",
    name: "MemberSignUp",
    beforeEnter: [isvalidUrl],
    component: () => import("../views/MemberSignUp.vue"),
  },
  {
    path: "/register",
    name: "NativeSignUp",
    beforeEnter: async (to, from, next) => {
      if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform()) {
        try {
          // Import helper function untuk get merchant_id
          const { getMerchantId } = await import('../config/tenant');
          const merchantId = getMerchantId();
          
          next({ name: 'MemberSignUp', params: { merchant_id: merchantId }, query: to.query });
          
        } catch (error) {
          // Final fallback
          const fallbackMerchantId = '9d45db1c-1406-496f-a175-9233e4bdf83d';
          next({ name: 'MemberSignUp', params: { merchant_id: fallbackMerchantId }, query: to.query });
        }
      } else {
        next({ name: 'NotFound' });
      }
    },
  },
  {
    path: "/systemlog",
    name: "SystemLog",
    component: () => import("../views/SystemLog.vue"),
    beforeEnter:requireBackoffice,
  },
  {
    path: "/featureflag/auditlog/:corporateId",
    name: "FeatureFlagAuditLog",
    meta: {
      requiresAuth: true
    },
    beforeEnter: [isRoot],
    component: () => import("../views/FeatureFlagAuditLog.vue"),
  },
  {
    path: "/logsapi",
    name: "ApiLog",
    component: () => import("../views/ApiLog.vue"),
    beforeEnter:requireBackoffice,
  },
  {
    path: "/membertier",
    name: "MemberTier",
    beforeEnter: [requireBackoffice, combineGuards("member-tier", [alreadyInCorporate])],
    component: () => import("../views/MemberTier.vue"),
  },
  {
    path: "/membertier/create",
    name: "MemberTierForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/MemberTierForm.vue"),
  },
  {
    path: "/membertier/edit/:id",
    name: "MemberTierForm.edit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/MemberTierForm.vue"),
  },
  {
    path: "/membertier-duration/create",
    name: "MemberTierDuration",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("tier-duration", [alreadyInCorporate])],
    component: () => import("../views/MemberTierDurationForm.vue"),
  },
  {
    path: "/membertiering/recalculate",
    name: "RecalculateMembertiering",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/MemberTier.vue"),
  },
  {
    path: "/membertier-duration/update",
    name: "MemberTierDurationEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("tier-duration", [alreadyInCorporate])],
    component: () => import("../views/MemberTierDurationForm.vue"),
  },
  {
    path: "/membertier/show/:id",
    name: "ShowMemberTier",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/ShowMemberTier.vue"),
  },
  {
    path: "/themes",
    name: "Themes",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/Themes.vue"),
  },
  {
    path: "/roles",
    name: "Role",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("user-roles", [alreadyInCorporate])],
    component: () => import("../views/Role.vue"),
  },
  {
    path: "/roles/create",
    name: "RoleForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/RoleForm.vue"),
  },
  {
    path: "/roles/:id",
    name: "RoleEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/RoleForm.vue"),
  },
  {
    path: "/transaction-report",
    name: "TransactionReport",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("transaction-report", [alreadyInCorporate, alreadyInBranch])],
    component: () => import("../views/TransactionReport.vue"),
  },
  {
    path: "/transaction-report/:id",
    name: "TransactionReportDetail",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("transaction-report", [alreadyInCorporate, alreadyInBranch])],
    component: () => import("../views/TransactionReportDetail.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/not-found.vue"),
  },
  {
    path: "/switch",
    name: "BusinessProfileSwitch",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice],
    component: () => import("../views/Switch.vue"),
  },
  {
    path: "/account",
    name: "Account",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice],
    component: () => import("../views/Account.vue"),
  },
  {
    path: "/client/account",
    name: "ClientAccount",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberAccount.vue"),
  },
  {
    path: "/corporate",
    name: "Corporate",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isRoot],
    component: () => import("../views/Corporate.vue"),
  },
  {
    path: "/corporate/create",
    name: "CorporateForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isRoot],
    component: () => import("../views/CorporateForm.vue"),
  },
  {
    path: "/corporate/edit/:id",
    name: "CorporateForm.edit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isRoot],
    component: () => import("../views/CorporateForm.vue"),
  },
  {
    path: '/feature-flags',
    name: 'FeatureFlagAdmin',
    meta: {
      requiresAuth: true
    },
    beforeEnter: [isRoot],
    component: () => import('../views/FeatureFlagAdmin.vue')
  },
  {
    path: '/point-settings',
    name: 'PointSystemSettings',
    meta: {
      requiresAuth: true
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import('../views/PointSystemSettings.vue')
  },
  {
    path: "/branch",
    name: "Branch",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("branch", [alreadyInCorporate])],
    component: () => import("../views/Branch.vue"),
  },
  {
    path: "/branch/:id",
    name: "BranchEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/BranchForm.vue"),
  },
  {
    path: "/branch/create",
    name: "BranchForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/BranchForm.vue"),
  },
  {
    path: "/branch/product",
    name: "BranchProduct",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("product", [alreadyInBranch])],
    component: () => import("../views/BranchProduct.vue"),
  },
  {
    path: "/merchant",
    name: "Merchant",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("merchant", [alreadyInBranch])],
    component: () => import("../views/Merchant.vue"),
  },
  {
    path: "/merchant/:id",
    name: "MerchantEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInBranch])],
    component: () => import("../views/MerchantForm.vue"),
  },
  {
    path: "/merchant/create",
    name: "MerchantForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInBranch])],
    component: () => import("../views/MerchantForm.vue"),
  },
  {
    path: "/user",
    name: "User",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("user", [alreadyInCorporate, alreadyInBranch])],
    component: () => import("../views/User.vue"),
  },
  {
    path: "/user/create",
    name: "UserForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch])],
    component: () => import("../views/UserForm.vue"),
  },
  {
    path: "/user/:id",
    name: "UserEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch])],
    component: () => import("../views/UserForm.vue"),
  },
  {
    path: "/member",
    name: "Member",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("member", [alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/Member.vue"),
  },
  {
    path: "/member/create",
    name: "MemberForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/MemberForm.vue"),
  },
  {
    path: "/member/:id",
    name: "MemberEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/MemberForm.vue"),
  },
  {
    path: "/member/transaction/:id",
    name: "MemberHistory",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/MemberHistory.vue"),
  },
  {
    path: "/member/tracking",
    name: "MemberTracking",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/MemberTracking.vue"),
  },
  {
    path: "/product",
    name: "Product",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("product", [alreadyInMerchant])],
    component: () => import("../views/Product.vue"),
  },
  {
    path: "/product/create",
    name: "ProductForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInMerchant])],
    component: () => import("../views/ProductForm.vue"),
  },
  {
    path: "/product/:id",
    name: "ProductEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInMerchant])],
    component: () => import("../views/ProductForm.vue"),
  },
  {
    path: "/benefit",
    name: "Benefit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("benefit", [alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/Benefit.vue"),
  },
  {
    path: "/benefit/create",
    name: "BenefitForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/BenefitForm.vue"),
  },
  {
    path: "/benefit/:id",
    name: "BenefitEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/BenefitForm.vue"),
  },
  {
    path: "/voucher",
    name: "Voucher",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("voucher")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await requirePointSystemActive()(to, from, async (flagResult) => {
          if (flagResult !== undefined && flagResult !== true) {
            return next(flagResult);
          }
          const merchantCheck = guardCheck([alreadyInCorporate, alreadyInMerchant])();
          return next(merchantCheck);
        });
      });
    }],
    component: () => import("../views/Voucher.vue"),
  },
  {
    path: "/voucher/create",
    name: "VoucherForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await requirePointSystemActive()(to, from, async (flagResult) => {
        if (flagResult !== undefined && flagResult !== true) {
          return next(flagResult);
        }
        const merchantCheck = guardCheck([alreadyInCorporate, alreadyInMerchant])();
        return next(merchantCheck);
      });
    }],
    component: () => import("../views/VoucherForm.vue"),
  },
  {
    path: "/voucher/:id",
    name: "VoucherEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await requirePointSystemActive()(to, from, async (flagResult) => {
        if (flagResult !== undefined && flagResult !== true) {
          return next(flagResult);
        }
        const merchantCheck = guardCheck([alreadyInCorporate, alreadyInMerchant])();
        return next(merchantCheck);
      });
    }],
    component: () => import("../views/VoucherForm.vue"),
  },
  {
    path: "/voucher/redeem",
    name: "VoucherRedeem",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("voucher_redeem")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await requirePointSystemActive()(to, from, async (flagResult) => {
          if (flagResult !== undefined && flagResult !== true) {
            return next(flagResult);
          }
          const merchantCheck = guardCheck([alreadyInMerchant])();
          return next(merchantCheck);
        });
      });
    }],
    component: () => import("../views/VoucherRedeem.vue"),
  },
  {
    path: "/voucher/gift/list",
    name: "VoucherGift",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("voucher")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await requirePointSystemActive()(to, from, async (flagResult) => {
          if (flagResult !== undefined && flagResult !== true) {
            return next(flagResult);
          }
          const merchantCheck = guardCheck([alreadyInMerchant])();
          return next(merchantCheck);
        });
      });
    }],
    component: () => import("../views/VoucherGift.vue"),
  },
  {
    path: "/voucher/gift",
    name: "VoucherGiftHistory",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("voucher")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await requirePointSystemActive()(to, from, async (flagResult) => {
          if (flagResult !== undefined && flagResult !== true) {
            return next(flagResult);
          }
          const merchantCheck = guardCheck([alreadyInMerchant])();
          return next(merchantCheck);
        });
      });
    }],
    component: () => import("../views/VoucherGiftHistory.vue"),
  },
  {
    path: "/voucher/gift/history/:id",
    name: "ShowVoucherGiftHistory",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await requirePointSystemActive()(to, from, async (flagResult) => {
        if (flagResult !== undefined && flagResult !== true) {
          return next(flagResult);
        }
        const merchantCheck = guardCheck([alreadyInMerchant])();
        return next(merchantCheck);
      });
    }],
    component: () => import("../views/ShowVoucherGiftHistory.vue"),
  },

  {
    path: "/transaction",
    name: "Transaction",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("transaction", [alreadyInMerchant])],
    component: () => import("../views/Transaction.vue"),
  },
  {
    path: "/transaction/create",
    name: "TransactionForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInMerchant])],
    component: () => import("../views/TransactionForm.vue"),
  },
  {
    path: "/transaction/:id",
    name: "TransactionEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInMerchant])],
    component: () => import("../views/TransactionForm.vue"),
  },
  {
    path: "/point-expiration",
    name: "PointExpiration",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("point_plan")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await requirePointSystemActive()(to, from, async (flagResult) => {
          if (flagResult !== undefined && flagResult !== true) {
            return next(flagResult);
          }
          const corporateCheck = guardCheck([alreadyInCorporate])();
          return next(corporateCheck);
        });
      });
    }],
    component: () => import("../views/PointExpiration.vue"),
  },
  {
    path: "/point/plan",
    name: "PointPlan",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      await checkModulePermission("point_plan")(to, from, async (permResult) => {
        if (permResult !== undefined && permResult !== true) {
          return next(permResult);
        }
        await checkPointPlanAccess()(to, from, next);
      });
    }],
    component: () => import("../views/PointPlan.vue"),
  },
  {
    path: "/point/plan/create",
    name: "PointPlanForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, checkPointPlanAccess()],
    component: () => import("../views/PointPlanForm.vue"),
  },
  {
    path: "/point/plan/:id",
    name: "PointPlanEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, checkPointPlanAccess()],
    component: () => import("../views/PointPlanForm.vue"),
  },
  {
    path: "/marketing",
    name: "MarketingRedirect",
    beforeEnter: [requireBackoffice],
    component: () => import("../views/MarketingRedirect.vue"),
  },
  {
    path: "/marketings",
    name: "Marketing",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      if (!store.state.Auth.selfLoaded) {
        try {
          await store.dispatch("Auth/auth_check");
        } catch (err) {
          console.error("Error loading user info:", err);
        }
      }
      next();
    }],
    component: () => import("../views/marketing/Marketing.vue"),
  },
  {
    path: "/marketings/:id",
    name: "MarketingDetail",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice],
    component: () => import("../views/marketing/MarketingDetail.vue"),
  },
  {
    path: "/campaign/:id",
    name: "CampaignDetail",
    meta: {
      requiresAuth: true,
    },
    component: () => import("../views/marketing/CampaignDetail.vue"),
  },
  {
    path: "/campaign",
    name: "Campaign",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("campaign", [alreadyInCorporate])],
    component: () => import("../views/Campaign.vue"),
  },
  {
    path: "/campaign/create",
    name: "CampaignForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice],
    component: () => import("../views/CampaignForm.vue"),
  },
  {
    path: "/campaign/:id",
    name: "CampaignEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate])],
    component: () => import("../views/CampaignForm.vue"),
  },
  {
    path: "/newsletter",
    name: "Newsletter",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, async (to, from, next) => {
      // Ensure auth data is loaded before running module permission check.
      // On hard refresh, Vuex store is empty so we must wait for auth_check.
      if (!store.state.Auth.selfLoaded) {
        try {
          await store.dispatch("Auth/auth_check");
        } catch (err) {
          if (err?.response?.status === 401) return next(false); // login ditangani session-expired
          if (err?.response) return next({ name: "PermissionDenied" });
          // offline: lanjut
        }
      }
      return combineGuards("newsletter", [alreadyInCorporate, alreadyInBranch, alreadyInMerchant])(to, from, next);
    }],
    component: () => import("../views/Newsletter/Newsletter.vue"),
  },
  {
    path: "/newsletter/create",
    name: "NewsletterForm",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/Newsletter/NewsletterForm.vue"),
  },
  {
    path: "/newsletter/:id/edit",
    name: "NewsletterEdit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, guardCheck([alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/Newsletter/NewsletterForm.vue"),
  },
  {
    path: "/newsletter/subscribers",
    name: "NewsletterSubscribers",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("newsletter", [alreadyInCorporate, alreadyInBranch, alreadyInMerchant])],
    component: () => import("../views/Newsletter/NewsletterSubscribers.vue"),
  },
  {
    path: "/inject/point",
    name: "PointManipulation",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("point-manipulation", [alreadyInCorporate, alreadyInMerchant, alreadyInBranch])],
    component: () => import("../views/InjectPoint.vue"),
  },
  // {
  //   path: "/user/access-token",
  //   name: "AccessToken",
  //   meta: {
  //     requiresAuth: true,
  //   },
  //   beforeEnter: combineGuards("access-token", [alreadyInMerchant]),
  //   component: () => import("../views/AccessToken.vue"),
  // },
  {
    path: "/tax-service",
    name: "TaxService",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [requireBackoffice, combineGuards("tax-service", [alreadyInCorporate, alreadyInMerchant, alreadyInBranch])],
    component: () => import("../views/TaxService.vue"),
  },
  // {
  //   path: "/user/authentication-url",
  //   name: "AuthenticationUrl",
  //   meta: {
  //     requiresAuth: true,
  //   },
  //   beforeEnter: guardCheck([alreadyInMerchant]),
  //   component: () => import("../views/AuthenticationUrl.vue"),
  // },
  {
    path: '/connection-apps',
    name: 'ConnectionApps',
    meta: {
      requiresAuth: true
    },
    beforeEnter: guardCheck([alreadyInCorporate]),
    component: () => import('../views/ConnectionApps/ConnectionAppsList.vue')
  },
  {
    path: '/promotions',
    name: 'Promotions',
    meta: {
      requiresAuth: true
    },
    beforeEnter: [requireBackoffice, combineGuards("promotions", [alreadyInCorporate])],
    component: () => import('../views/Promotions/Promotions.vue')
  },
  {
    path: '/connection-apps/setup',
    name: 'ConnectionSetup',
    meta: {
      requiresAuth: true
    },
    beforeEnter: guardCheck([alreadyInCorporate]),
    component: () => import('../views/ConnectionApps/ConnectionSetupWizard.vue')
  },
  {
    path: '/connection-apps/custom-token/create',
    name: 'CustomTokenCreate',
    meta: {
      requiresAuth: true
    },
    beforeEnter: guardCheck([alreadyInCorporate]),
    component: () => import('../views/ConnectionApps/CustomToken/CustomTokenCreate.vue')
  },
  {
    path: '/connection-apps/:id',
    name: 'ConnectionDetail',
    meta: {
      requiresAuth: true
    },
    beforeEnter: guardCheck([alreadyInCorporate]),
    component: () => import('../views/ConnectionApps/ConnectionDetail.vue')
  },

  //Path Member
  {
    path: "/client/dashboard",
    name: "client_dashboard",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberDashboard"),
  },
  {
    path: "/client/transaction-history",
    name: "client_transaction",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/TransactionHistory.vue"),
  },
  {
    path: "/point-info",
    name: "PointInfo",
    meta: {
      requiresAuth: true,
      hideNavbar: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/PointInfo.vue"),
  },
  {
    path: "/newsletters/unsubscribe",
    name: "newsletter_unsubscribe",
    meta: {
      requiresAuth: false,
    },
    component: () => import("../views/member/NewsletterUnsubscribe.vue"),
  },
  {
    path: "/client/newsletters",
    name: "client_newsletters",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/NewsletterList.vue"),
  },
  {
    path: "/client/newsletters/:id",
    name: "client_newsletter_detail",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/NewsletterDetail.vue"),
  },
  {
    path: "/client/voucher",
    name: "client_voucher",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/Vouchers"),
  },
  {
    path: "/client/deals",
    name: "client_deals",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberDeals"),
  },
  {
    path: "/client/qrcode",
    name: "client_qrcode",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: (to, from, next) => {
      if (isMember()) {
        if (triggeredComponent != "navbar-mobile.vue") {
          next();
        }
      } else {
        next(false);
      }
    },
    component: () => import("../views/member/MemberQrCode"),
  },
  {
    path: "/client/deals/redeem/:id_voucher",
    name: "client_deals_redeem",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberDealsRedeem"),
  },
  {
    path: "/client/benefit",
    name: "ClientBenefit",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberBenefit.vue"),
  },
  {
    path: "/client/tier",
    name: "ClientTier",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [isMember],
    component: () => import("../views/member/MemberBenefit.vue"),
  },
  {
    path: "/policies",
    name: "Policies",
    meta: {
      requiresAuth: true,
    },
    beforeEnter: combineGuards("policies", [alreadyInCorporate]),
    component: () => import("../views/Policies.vue"),
  },
  {
    path: "/permission-denied",
    name: "PermissionDenied",
    meta: {
      requiresAuth: true,
    },
    component: () => import("../views/PermissionDenied.vue"),
  },
  // Extranet Login Route (standalone, no layout)
  {
    path: '/extranet/login',
    name: 'ExtranetLogin',
    component: () => import('@/views/extranet/ExtranetLogin.vue'),
    meta: { requiresAuth: false }
  },
  // Extranet Forgot Password Route (standalone, no layout)
  {
    path: '/extranet/forgot-password',
    name: 'ExtranetForgotPassword',
    component: () => import('@/views/extranet/ExtranetForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  // Extranet Routes
  {
    path: '/extranet',
    component: () => import('@/layouts/ExtranetLayout.vue'),
    meta: { requiresAuth: true },
    beforeEnter: [isRoot],
    children: [
      {
        path: '',
        redirect: '/extranet/dashboard'
      },
      {
        path: 'dashboard',
        name: 'ExtranetDashboard',
        component: () => import('@/views/extranet/ExtranetDashboard.vue')
      },
      {
        path: 'corporate',
        name: 'ExtranetCorporate',
        component: () => import('@/views/extranet/ExtranetCorporate.vue')
      },
      {
        path: 'corporate/create',
        name: 'ExtranetCorporateForm',
        component: () => import('@/views/extranet/ExtranetCorporateForm.vue')
      },
      {
        path: 'corporate/edit/:id',
        name: 'ExtranetCorporateEdit',
        component: () => import('@/views/extranet/ExtranetCorporateForm.vue')
      },
      {
        path: 'system-log',
        name: 'ExtranetSystemLog',
        component: () => import('@/views/extranet/ExtranetSystemLog.vue')
      },
      {
        path: 'api-log',
        name: 'ExtranetApiLog',
        component: () => import('@/views/extranet/ExtranetApiLog.vue')
      },
      {
        path: 'feature-flags',
        name: 'ExtranetFeatureFlags',
        component: () => import('@/views/extranet/ExtranetFeatureFlags.vue')
      },
      {
        path: 'feature-flags/detail/:featureKey',
        name: 'ExtranetFeatureFlagDetail',
        component: () => import('@/views/extranet/ExtranetFeatureFlagDetail.vue')
      },
      {
        path: 'feature-flags/audit-log/:corporateId?',
        name: 'ExtranetFeatureFlagAuditLog',
        component: () => import('@/views/extranet/ExtranetFeatureFlagAudit.vue')
      },
      {
        path: 'tenant-admin',
        name: 'ExtranetTenantAdmin',
        component: () => import('@/views/extranet/ExtranetTenantAdmin.vue')
      },
      {
        path: 'branches',
        name: 'ExtranetBranches',
        component: () => import('@/views/extranet/ExtranetBranch.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'merchants',
        name: 'ExtranetMerchants',
        component: () => import('@/views/extranet/ExtranetMerchant.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/branches',
        name: 'ExtranetBranch',
        component: () => import('@/views/extranet/ExtranetBranch.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/branches/create',
        name: 'ExtranetBranchForm',
        component: () => import('@/views/extranet/ExtranetBranchForm.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/branches/edit/:id',
        name: 'ExtranetBranchEdit',
        component: () => import('@/views/extranet/ExtranetBranchForm.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/merchants',
        name: 'ExtranetMerchant',
        component: () => import('@/views/extranet/ExtranetMerchant.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/merchants/create',
        name: 'ExtranetMerchantForm',
        component: () => import('@/views/extranet/ExtranetMerchantForm.vue'),
        beforeEnter: requireTenantSelected()
      },
      {
        path: 'tenant-admin/merchants/edit/:id',
        name: 'ExtranetMerchantEdit',
        component: () => import('@/views/extranet/ExtranetMerchantForm.vue'),
        beforeEnter: requireTenantSelected()
      }
    ]
  }
];

/**
 * Checks if the current user has "root" level access.
 * 
 * @returns {Promise<boolean|Object>} Returns true if user is root, otherwise returns redirect to Home.
 */
async function isRoot() {
  let userLevel;
  const user = await store.getters["Auth/getUser"];
  if (user && user.level) {
    userLevel = user.level;
  } else {
    userLevel = localStorage.getItem("level");
  }
  return userLevel === "root" ? true : { name: "Home" };
}

/**
 * Checks if the current user has "member" level access.
 * 
 * @returns {Promise<boolean|Object>} Returns true if user is member, otherwise returns redirect to Home.
 */
async function isMember() {
  const level = localStorage.getItem("level");
  return (level && level.includes("member")) ? true : { name: "Home" };
}

/**
 * Validates if the merchant_id in the URL is valid.
 * 
 * @param {Object} to - The target Route Object being navigated to.
 * @returns {Promise<boolean|Object>} Returns true if URL is valid, otherwise returns redirect to NotFound.
 */
function isvalidUrl(to) {
  // Bypass validation for native apps to avoid connectivity issues
  if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform()) {
    return true;
  }
  
  return store
    .dispatch("Auth/corporateUrlCheck", to.params.merchant_id)
    .then((res) => (res ? true : { name: "NotFound" }))
    .catch((error) => {
      console.error('[Router] URL validation failed:', error);
      return { name: "NotFound" };
    });
}

/**
 * Checks if the user is currently operating in a Corporate context.
 * 
 * @returns {boolean} True if in Corporate context.
 */
function alreadyInCorporate() {
  const selected_business_profile = JSON.parse(localStorage.getItem("selected_business_profile"));
  return (
    selected_business_profile.corporate_id != null &&
    selected_business_profile.branch_id == null &&
    selected_business_profile.merchant_id == null
  );
}

/**
 * Checks if the user is currently operating in a Branch context.
 * 
 * @returns {boolean} True if in Branch context.
 */
function alreadyInBranch() {
  const selected_business_profile = JSON.parse(localStorage.getItem("selected_business_profile"));
  return (
    selected_business_profile.corporate_id != null &&
    selected_business_profile.branch_id != null &&
    selected_business_profile.merchant_id == null
  );
}

/**
 * Checks if the user is currently operating in a Merchant context.
 * 
 * @returns {boolean} True if in Merchant context.
 */
function alreadyInMerchant() {
  const selected_business_profile = JSON.parse(localStorage.getItem("selected_business_profile"));
  return (
    selected_business_profile.corporate_id != null &&
    selected_business_profile.branch_id != null &&
    selected_business_profile.merchant_id != null
  );
}

function requireBackoffice(to, from, next) {
  const user = store.state.Auth.currUser || store.getters["Auth/getUser"];
  const level = user?.level || localStorage.getItem("level");

  if (level === "member") {
    return next({ name: "client_dashboard" });
  }

  // Sebaiknya cek allowlist level backoffice yang memang valid di aplikasi,
  // bukan menganggap semua level selain "member" sebagai admin.
  const backofficeLevels = ["root", "corporate", "branch", "merchant"];

  if (!backofficeLevels.includes(level)) {
    return next({ name: "PermissionDenied" });
  }

  return next();
}


const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash };
    }
    return { top: 0, left: 0 };
  },
});


router.beforeEach(async (to, from, next) => {
  const isExtranet = isExtranetHostname();
  // Ensure session is restored/hydrated before any navigation checks
  if (!store.state.Auth.isHydrated) {
    try {
      await store.dispatch("Auth/restoreSession");
    } catch (e) {
      console.warn("[Router] Error during restoreSession:", e);
    }
  }

  const isLoggedIn = store.getters["Auth/isLoggedIn"];

  // Splash flag (verifikasi sesi sekarang ditangani di blok requiresAuth di bawah)
  if (isMobile && !isAppInitialized) {
    isAppInitialized = true;
    splashPendingHide = true;
  }

  // If user is already logged in and tries to access login/root pages, redirect to dashboard.
  // Bypass redirect if this is an external Booking Engine / web_temp_access integration request.
  const isAuthEntryRoute =
    to.path === "/" ||
    to.path === "/login" ||
    to.path === "/member/login" ||
    to.name === "Login" ||
    to.name === "MemberLogin";

  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const isWebTempAccess =
    !isMobile && (
      Boolean(to.query.merchant_id || searchParams?.get("merchant_id")) ||
      Boolean(to.query.ref || searchParams?.get("ref")) ||
      to.query.type === "web_temp_access" ||
      searchParams?.get("type") === "web_temp_access" ||
      (Boolean(to.query.corporate_id || searchParams?.get("corporate_id")) &&
        Boolean(to.query.dashboard || searchParams?.get("dashboard")))
    );

  if (isLoggedIn && isAuthEntryRoute && !isExtranet && !isWebTempAccess) {
    const redirectPath = localStorage.getItem("redirect_after_login");
    if (redirectPath && redirectPath.startsWith("/client/")) {
      localStorage.removeItem("redirect_after_login");
      return next(redirectPath);
    }
    const level = localStorage.getItem("level");
    if (level === "member") {
      return next({ name: "client_dashboard" });
    }
    return next({ name: "Home" });
  }

  // Protected routes
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (isLoggedIn) {
      // Verifikasi ke server sekali per load, SEBELUM komponen halaman dibuat.
      // Saat offline dilewati: sesi dipertahankan.
      if (!isSessionVerified() && store.state.network?.isOnline !== false) {
        try {
          await store.dispatch("Auth/auth_check");
          markSessionVerified();
        } catch (err) {
          if (err?.response?.status === 401) {
            if (to.fullPath.startsWith("/client/")) {
              localStorage.setItem("redirect_after_login", to.fullPath);
            }
            const target = await expireSession(to.fullPath);
            return next(target);
          }
          // Tanpa response (offline/timeout) atau 403/5xx: tetap masuk
        }
      }
      return next();
    }

    // Belum login
    if (to.fullPath.startsWith("/client/")) {
      localStorage.setItem("redirect_after_login", to.fullPath);
    }
    await setSwipeBackEnabledSafe(false);
    return next(resolveLoginPath(to.path));
  }

  // Public routes (kode asli yang sempat hilang)
  const isAuthDest =
    to.path === "/login" ||
    to.path === "/member/login" ||
    to.path === "/backoffice/login" ||
    to.path === "/extranet/login" ||
    to.path === "/forgot-password" ||
    to.path === "/register" ||
    (to.path && to.path.startsWith("/reset-password")) ||
    to.name === "Login" ||
    to.name === "MemberLogin" ||
    to.name === "BackofficeLogin" ||
    to.name === "ForgotPassword";
  await setSwipeBackEnabledSafe(!isAuthDest);

  // Extranet subdomain yang membuka root atau /login diarahkan ke extranet login
  if (isExtranet && (to.path === "/" || to.path === "/login")) {
    return next("/extranet/login");
  }
  return next();
});

// Hide splash hanya setelah navigasi pertama selesai dan DOM sudah siap
router.afterEach(() => {
  nextTick(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      const memberMain = document.querySelector('.member-main');
      if (memberMain) {
        memberMain.scrollTop = 0;
      }
    }
  });

  if (isMobile && splashPendingHide) {
    splashPendingHide = false;
    nextTick(() => {
      hideSplash(300);
    });
  }
});

export default router;